import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown, Check, Plus, Send, Search, MapPin, Layers, Calculator, CalendarRange } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { landingCopy } from "../locales/landing";
import LatestNewsSection from "../components/LatestNewsSection";
import PromotionSlot from "../components/PromotionSlot";
import { serviceCopy } from "../seo/services";
import "./Lending.css";

export default function Lending() {
  const { language } = useLanguage();
  const c = landingCopy[language];
  const [example, setExample] = useState<0 | 1>(0);
  const preview = c.preview.examples[example];

  useEffect(() => {
    const anchor = window.location.hash.slice(1);
    if (!["search", "notifications", "analysis", "start"].includes(anchor)) return;
    const frame = requestAnimationFrame(() =>
      document.getElementById(anchor)?.scrollIntoView(),
    );
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <div className="landing-shell" lang={language}>
      <section className="landing-hero" aria-labelledby="landing-title">
        <div className="landing-container">
          <div className="landing-hero-heading">
            <p className="landing-eyebrow">{c.eyebrow}</p>
            <span className="landing-edition">{c.edition}</span>
          </div>
          <div className="landing-hero-grid">
            <div className="landing-hero-copy">
              <h1 id="landing-title">
                {c.title}
                <br />
                <em>{c.titleAccent}</em>
              </h1>
              <p className="landing-lead">{c.intro}</p>
              <div className="landing-actions">
                <Link to="/lots" className="landing-button">
                  {c.searchCta}
                  <ArrowRight aria-hidden="true" />
                </Link>
                <a href="#notifications" className="landing-text-link">
                  {c.howCta}
                  <ArrowDown aria-hidden="true" />
                </a>
              </div>
              <p className="landing-small-note">{c.accessNote}</p>
            </div>
            <figure className="landing-preview">
              <figcaption className="landing-preview-caption">
                <span>{c.preview.label}</span>
                <span>G-Launcher / Telegram</span>
              </figcaption>
              <div
                className="landing-preview-switch"
                role="group"
                aria-label={c.preview.switchLabel}
              >
                {c.preview.examples.map((item, index) => (
                  <button
                    key={index}
                    type="button"
                    aria-pressed={example === index}
                    aria-controls="landing-notification-example"
                    onClick={() => setExample(index as 0 | 1)}
                  >
                    {item.tab}
                  </button>
                ))}
              </div>
              <div
                id="landing-notification-example"
                className="landing-preview-body"
                aria-live="polite"
                aria-atomic="true"
              >
                <div className="landing-preview-context">
                  <span className="landing-preview-index">0{example + 1}</span>
                  <div>
                    <span className="landing-micro-label">
                      {preview.contextLabel}
                    </span>
                    <p>{preview.context}</p>
                  </div>
                </div>
                <div className="landing-preview-connector" aria-hidden="true">
                  <ArrowDown />
                </div>
                <div className="landing-message">
                  <div className="landing-message-sender">
                    <Send aria-hidden="true" />
                    <strong>G-Launcher</strong>
                    <span>{c.preview.channel}</span>
                  </div>
                  <p className="landing-message-title">{preview.title}</p>
                  <p>{preview.body}</p>
                  <div className="landing-message-detail">{preview.detail}</div>
                  <p className="landing-message-footer">
                    {c.preview.open}
                    <ArrowRight aria-hidden="true" />
                  </p>
                </div>
                <p className="landing-preview-note">{preview.note}</p>
              </div>
            </figure>
          </div>
          <nav className="landing-contents" aria-label={c.contentsLabel}>
            {c.contents.map((item, index) => (
              <a key={item.anchor} href={`#${item.anchor}`}>
                <span>0{index + 1}</span>
                {item.label}
                <ArrowDown aria-hidden="true" />
              </a>
            ))}
          </nav>
        </div>
      </section>
      <section
        id="search"
        className="landing-section"
        aria-labelledby="landing-search-title"
      >
        <div className="landing-container">
          <div className="landing-search-grid">
            <div className="landing-section-intro">
              <p className="landing-eyebrow">01 / {c.search.eyebrow}</p>
              <h2 id="landing-search-title">{c.search.title}</h2>
              <p>{c.search.intro}</p>
              <Link to="/lots" className="landing-text-link">
                {c.search.link}
                <ArrowRight aria-hidden="true" />
              </Link>
            </div>
            <div className="landing-search-detail">
              <div className="landing-category-note">
                <div className="landing-category-heading">
                  <span>{c.search.categoryLabel}</span>
                  <span className="landing-category-mark" aria-hidden="true">
                    <Layers />
                  </span>
                </div>
                <h3>{c.search.categoryTitle}</h3>
                <p>{c.search.categoryText}</p>
                <div
                  className="landing-category-sequence"
                  aria-label={c.search.hierarchyLabel}
                >
                  {c.search.hierarchy.map((label, i) => (
                    <React.Fragment key={label}>
                      {i > 0 && <ArrowRight aria-hidden="true" />}
                      <span>{label}</span>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <figure className="landing-search-sample">
            <figcaption className="landing-sample-caption">
              <span>{c.search.eyebrow}</span>
              <span className="landing-sample-label">{c.search.sample.label}</span>
            </figcaption>
            <div className="landing-sample-filters" aria-label={c.search.hierarchyLabel}>
              <span className="landing-sample-query"><Search aria-hidden="true" />{c.search.sample.query}</span>
              <span className="landing-sample-region"><MapPin aria-hidden="true" />{c.search.sample.region}</span>
              <span className="landing-sample-category"><Layers aria-hidden="true" />{c.search.sample.category}</span>
            </div>
            <p className="landing-scroll-hint"><ArrowRight aria-hidden="true" />{c.search.sample.scrollHint}</p>
            <div className="landing-table-scroll" role="region" aria-label={c.search.sample.label} tabIndex={0}>
              <table className="landing-sample-table">
                <thead>
                  <tr>{c.search.sample.columns.map((column) => <th key={column} scope="col">{column}</th>)}</tr>
                </thead>
                <tbody>
                  {c.search.sample.rows.map((row) => (
                    <tr key={row.number}>
                      <td className="landing-sample-number">{row.number}</td>
                      <td><strong>{row.title}</strong><span className="landing-sample-customer">{row.customer}</span></td>
                      <td className="landing-sample-amount">{row.amount}</td>
                      <td><span className="landing-sample-status">{row.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="landing-sample-footer">
              <p>{c.search.sample.note}</p>
              <Link to="/lots" className="landing-text-link">{c.search.link}<ArrowRight aria-hidden="true" /></Link>
            </div>
          </figure>
          <dl className="landing-filter-list">
            {c.search.filters.map((filter) => <div key={filter.title}><dt>{filter.title}</dt><dd>{filter.text}</dd></div>)}
          </dl>
        </div>
      </section>
      <section
        id="notifications"
        className="landing-notifications landing-section"
        aria-labelledby="landing-notifications-title"
      >
        <div className="landing-container">
          <div className="landing-section-heading">
            <p className="landing-eyebrow">02 / {c.notifications.eyebrow}</p>
            <h2 id="landing-notifications-title">{c.notifications.title}</h2>
            <p>{c.notifications.intro}</p>
          </div>
          <div className="landing-notification-grid">
            {c.notifications.services.map((service, index) => (
              <article
                className="landing-notification-service"
                key={service.title}
              >
                <span className="landing-service-type">{service.tag}</span>
                <h3>{service.title}</h3>
                <p>{service.text}</p>
                <ol>
                  {service.steps.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
                <Link
                  to={index === 0 ? "/lots?tab=alerts" : "/tracker"}
                  className="landing-text-link"
                >
                  {service.link}
                  <ArrowRight aria-hidden="true" />
                </Link>
              </article>
            ))}
          </div>
          <p className="landing-telegram-note">
            <Send aria-hidden="true" />
            <span>{c.notifications.note}</span>
          </p>
        </div>
      </section>
      <section
        id="analysis"
        className="landing-section"
        aria-labelledby="landing-analysis-title"
      >
        <div className="landing-container">
          <div className="landing-analysis-heading">
            <div>
              <p className="landing-eyebrow">03 / {c.analysis.eyebrow}</p>
              <h2 id="landing-analysis-title">{c.analysis.title}</h2>
            </div>
            <p>{c.analysis.intro}</p>
          </div>
          <div className="landing-finance-grid">
            {c.analysis.previews.map((preview, index) => {
              const service = c.analysis.services[index + 1];
              const Icon = index === 0 ? Calculator : CalendarRange;
              return (
                <article className="landing-finance-card" key={service.path}>
                  <div className="landing-finance-heading"><Icon aria-hidden="true" /><h3>{service.title}</h3></div>
                  <p>{service.text}</p>
                  <div className="landing-finance-example" aria-label={c.analysis.previewLabel}>
                    <div>
                      <span className="landing-micro-label">{preview.inputTitle}</span>
                      <ul>{preview.inputs.map((input) => <li key={input}>{input}</li>)}</ul>
                    </div>
                    <ArrowDown className="landing-finance-arrow" aria-hidden="true" />
                    <div className="landing-finance-output">
                      <span className="landing-micro-label">{preview.outputTitle}</span>
                      <ul>{preview.outputs.map((output) => <li key={output}><Check aria-hidden="true" />{output}</li>)}</ul>
                    </div>
                  </div>
                  <Link to={service.path} className="landing-text-link">{preview.link}<ArrowRight aria-hidden="true" /></Link>
                </article>
              );
            })}
          </div>
          <div className="landing-service-list">
            {c.analysis.services.slice(0, 1).map((service) => (
              <Link
                to={service.path}
                className="landing-service-row"
                key={service.path}
              >
                <h3>{service.title}</h3>
                <p>{service.text}</p>
                <ArrowRight aria-hidden="true" />
              </Link>
            ))}
          </div>
          <p className="landing-analysis-note">{c.analysis.note}</p>
        </div>
      </section>
      <section
        id="start"
        className="landing-start landing-section"
        aria-labelledby="landing-start-title"
      >
        <div className="landing-container landing-start-grid">
          <div>
            <p className="landing-eyebrow">{c.start.eyebrow}</p>
            <h2 id="landing-start-title">{c.start.title}</h2>
            <p>{c.start.intro}</p>
            <div className="landing-actions">
              <Link to="/lots" className="landing-button">
                {c.searchCta}
                <ArrowRight aria-hidden="true" />
              </Link>
              <Link
                to="/plans"
                className="landing-text-link"
              >
                {c.start.plans}
                <ArrowRight aria-hidden="true" />
              </Link>
            </div>
          </div>
          <ol className="landing-steps">
            {c.start.steps.map((step, index) => (
              <li key={step.title}>
                <span>0{index + 1}</span>
                <div>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </div>
                <Check aria-hidden="true" />
              </li>
            ))}
          </ol>
        </div>
      </section>
      <PromotionSlot placement="home_lower" className="landing-container" />
      <section className="landing-section" aria-labelledby="landing-faq-title">
        <div className="landing-container landing-faq-grid">
          <div>
            <p className="landing-eyebrow">{c.faq.eyebrow}</p>
            <h2 id="landing-faq-title">{c.faq.title}</h2>
            <Link to="/contacts" className="landing-text-link">
              {c.faq.contact}
              <ArrowRight aria-hidden="true" />
            </Link>
          </div>
          <div className="landing-faq-list">
            {c.faq.items.map((item) => (
              <details key={item.question}>
                <summary>
                  {item.question}
                  <Plus aria-hidden="true" />
                </summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
      <section className="landing-section landing-guides" aria-labelledby="service-guides-title">
        <div className="landing-container">
          <h2 id="service-guides-title">{language === 'kk' ? 'Қызметтер туралы толығырақ' : 'Подробнее об услугах'}</h2>
          <ul className="landing-guide-list">
            {Object.entries(serviceCopy[language]).map(([slug, item]) => <li key={slug}><Link to={`/services/${slug}`}>{item.title}<ArrowRight aria-hidden="true" /></Link></li>)}
          </ul>
        </div>
      </section>
      <LatestNewsSection />
    </div>
  );
}
